var _hooksUseGroupSubscriptionDataQuery;
import { useAsync, useMountEffect, useSyncedRef } from "@react-hookz/web";
import axios from "axios";
import { getUnixTime } from "date-fns";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useMemo } from "react";
import { fetchQuery, graphql, useRelayEnvironment } from "react-relay";
import { useSdkContext } from "~/components/contexts";
import { useFallbackStorageValue } from "~/lib/hooks";
import { useControlledPromise } from "~/lib/utils";
import { fetchCorporateSubscriberTier } from "./apis";
import { GroupSubscriberTooltip } from "./component";
export const useGroupSubscriberTooltipRender = () => {
  const {
    user
  } = useSdkContext();
  const {
    remove: removeGroupSubscriberData,
    set: setGroupSubscriberData,
    value: groupSubscriberData
  } = useFallbackStorageValue("groupSubscriberTooltipData");
  const acquisitionConfigState = useAsyncAcquisitionConfig();
  const corporateTierState = useAsyncHasCorporateSubscriberTier();
  const syncedGroupSubscriberData = useSyncedRef(groupSubscriberData);
  const {
    promise,
    reject,
    resolve
  } = useControlledPromise();
  useEffect(() => {
    var _syncedGroupSubscribe, _syncedGroupSubscribe2;
    if (isPromiseIncomplete(acquisitionConfigState) || isPromiseIncomplete(corporateTierState)) return;else if (!user || isPromiseWithError(acquisitionConfigState) || isPromiseWithError(corporateTierState)) return reject.current();
    const acquisitionConfigStateResult = acquisitionConfigState.result;
    const corporateTierStateResult = corporateTierState.result;
    if (!acquisitionConfigStateResult || !corporateTierStateResult) return reject.current();
    if ((syncedGroupSubscriberData === null || syncedGroupSubscriberData === void 0 || (_syncedGroupSubscribe = syncedGroupSubscriberData.current) === null || _syncedGroupSubscribe === void 0 ? void 0 : _syncedGroupSubscribe.version) !== acquisitionConfigStateResult.tooltip.version) removeGroupSubscriberData();
    const currentGroupSubscriberData = (_syncedGroupSubscribe2 = syncedGroupSubscriberData.current) !== null && _syncedGroupSubscribe2 !== void 0 ? _syncedGroupSubscribe2 : {
      intervalData: [{
        intervalTime: 0,
        shown: false
      }, {
        intervalTime: 604_800,
        shown: false
      }, {
        intervalTime: 1_209_600,
        shown: false
      }, {
        intervalTime: 1_814_400,
        shown: false
      }, {
        intervalTime: 2_419_200,
        shown: false
      }],
      lastShownTimeStamp: getUnixTime(Date.now()),
      version: acquisitionConfigStateResult.tooltip.version
    };
    const secondsSinceLastShownTimestamp = getUnixTime(Date.now()) - currentGroupSubscriberData.lastShownTimeStamp;
    if (!currentGroupSubscriberData.intervalData) return reject.current();
    for (const [index] of currentGroupSubscriberData.intervalData.entries()) {
      const validIntervalData = currentGroupSubscriberData.intervalData;
      if (secondsSinceLastShownTimestamp >= currentGroupSubscriberData.intervalData[index].intervalTime && !currentGroupSubscriberData.intervalData[index].shown) {
        return resolve.current({
          node: /*#__PURE__*/React.createElement(GroupSubscriberTooltip, {
            message: `${acquisitionConfigStateResult.tooltip.message}`,
            onImpression: () => {
              setGroupSubscriberData(() => {
                var _currentGroupSubscrib;
                const clonedIntervals = [...validIntervalData];
                clonedIntervals[index] = {
                  intervalTime: validIntervalData[index].intervalTime,
                  shown: true
                };
                return {
                  intervalData: clonedIntervals,
                  lastShownTimeStamp: currentGroupSubscriberData.lastShownTimeStamp,
                  version: (_currentGroupSubscrib = currentGroupSubscriberData.version) !== null && _currentGroupSubscrib !== void 0 ? _currentGroupSubscrib : ""
                };
              });
            }
          })
        });
      }
    }
    return reject.current();
  }, [acquisitionConfigState, corporateTierState, reject, removeGroupSubscriberData, resolve, setGroupSubscriberData, syncedGroupSubscriberData, user, promise]);
  return {
    groupSubscriberTooltipRender: promise
  };
};
const isPromiseIncomplete = state => !state || state.status === "loading" || state.status === "not-executed";
const isPromiseWithError = state => state.status === "error";
const useAsyncAcquisitionConfig = () => {
  const environment = useRelayEnvironment();
  const {
    config
  } = useSdkContext();
  const [state, action] = useAsync(async () => {
    var _response$appConfig, _response$appConfig2;
    const response = await fetchQuery(environment, _hooksUseGroupSubscriptionDataQuery !== void 0 ? _hooksUseGroupSubscriptionDataQuery : (_hooksUseGroupSubscriptionDataQuery = require("../../../../queries/__generated__/hooksUseGroupSubscriptionDataQuery.graphql"), _hooksUseGroupSubscriptionDataQuery.hash && _hooksUseGroupSubscriptionDataQuery.hash !== "10b91bd845ddc4c70e045441fbd4454d" && console.error("The definition of 'hooksUseGroupSubscriptionDataQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _hooksUseGroupSubscriptionDataQuery), {}).toPromise();
    if (response !== null && response !== void 0 && (_response$appConfig = response.appConfig) !== null && _response$appConfig !== void 0 && _response$appConfig.json) return (response === null || response === void 0 || (_response$appConfig2 = response.appConfig) === null || _response$appConfig2 === void 0 ? void 0 : _response$appConfig2.json)[config.environment];
    return;
  });
  useMountEffect(() => {
    void action.execute();
  });
  return state;
};
const useAsyncHasCorporateSubscriberTier = () => {
  const {
    config,
    user
  } = useSdkContext();
  const {
    remove: removeCorporateSubscriberTier,
    set: setCorporateSubscriberTier,
    value: corporateSubscriberTier
  } = useFallbackStorageValue("corp_subscriber_tier");
  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: config.corporateSubscriber.host,
      headers: {
        apikey: config.graphql.apiKey
      }
    });
    return instance;
  }, [config]);

  // This state contains tells whether user belongs to the tiers below
  const [state, action] = useAsync(async () => {
    var _corporateSubscriberT, _data$Tier;
    const tiers = new Set(["T2", "T3", "T4", "C-suite"]);
    if (!(user !== null && user !== void 0 && user.useremail)) return false;
    if (corporateSubscriberTier && (corporateSubscriberTier === null || corporateSubscriberTier === void 0 ? void 0 : corporateSubscriberTier["Users Email Address"]) !== (user === null || user === void 0 ? void 0 : user.useremail)) removeCorporateSubscriberTier();
    if (!isEmpty(corporateSubscriberTier)) return tiers.has((_corporateSubscriberT = corporateSubscriberTier === null || corporateSubscriberTier === void 0 ? void 0 : corporateSubscriberTier.Tier) !== null && _corporateSubscriberT !== void 0 ? _corporateSubscriberT : "");
    const data = await fetchCorporateSubscriberTier(axiosInstance, user === null || user === void 0 ? void 0 : user.useremail);
    if (isEmpty(corporateSubscriberTier) && data) setCorporateSubscriberTier(data);
    return tiers.has((_data$Tier = data === null || data === void 0 ? void 0 : data.Tier) !== null && _data$Tier !== void 0 ? _data$Tier : "");
  });
  useMountEffect(() => void action.execute());
  return state;
};